import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { publicApi, setPToken, errMsg } from "@/lib/api";
import { getOrigin } from "@/lib/origin";
import { UFS, maskPhone } from "@/lib/format";

const Field = ({ id, label, children }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-[16px] font-semibold">{label}</Label>
    {children}
  </div>
);
const inputCls = "h-12 text-[17px]";

const FORM_KEY = "zapiudp_regform";
const loadForm = () => {
  try {
    return JSON.parse(sessionStorage.getItem(FORM_KEY)) || null;
  } catch {
    return null;
  }
};

const RegisterForm = ({ onDone }) => {
  const [f, setF] = useState(() => loadForm() || { name: "", whatsapp: "", city: "", state: "", consent: false });
  useEffect(() => {
    sessionStorage.setItem(FORM_KEY, JSON.stringify(f));
  }, [f]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const valid = f.name.trim().split(/\s+/).length >= 2 && f.whatsapp.replace(/\D/g, "").length >= 10 && f.city.trim().length >= 2 && f.state && f.consent;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await publicApi.post("/public/register", { ...f, whatsapp: f.whatsapp.replace(/\D/g, ""), origin: getOrigin() });
      setPToken(data.token);
      sessionStorage.removeItem(FORM_KEY);
      await onDone(data.returning ? "return" : "register");
    } catch (err) {
      setError(errMsg(err, "Sem conexão. Seus dados e sua mensagem continuam guardados; tente novamente."));
    }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="register-form">
      <Field id="reg-name" label="Nome completo"><Input id="reg-name" data-testid="register-name" className={inputCls} value={f.name} onChange={set("name")} autoComplete="name" placeholder="Nome e sobrenome" /></Field>
      <Field id="reg-wa" label="WhatsApp com DDD"><Input id="reg-wa" data-testid="register-whatsapp" className={inputCls} inputMode="tel" placeholder="(11) 91234-5678" value={maskPhone(f.whatsapp)} onChange={(e) => setF({ ...f, whatsapp: e.target.value })} /></Field>
      <div className="grid grid-cols-[1fr_110px] gap-3">
        <Field id="reg-city" label="Cidade"><Input id="reg-city" data-testid="register-city" className={inputCls} value={f.city} onChange={set("city")} /></Field>
        <Field id="reg-uf" label="Estado">
          <Select value={f.state} onValueChange={set("state")}>
            <SelectTrigger id="reg-uf" data-testid="register-state" className={inputCls}><SelectValue placeholder="UF" /></SelectTrigger>
            <SelectContent className="max-h-72">{UFS.map((u) => <SelectItem key={u} value={u} data-testid={`register-state-${u}`} className="text-base">{u}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>
      <label className="flex items-start gap-3 rounded-md bg-zap-mist p-3 text-[15px] leading-snug">
        <Checkbox data-testid="register-consent" checked={f.consent} onCheckedChange={(v) => setF({ ...f, consent: !!v })} className="mt-0.5 h-6 w-6" />
        <span>Autorizo a Igreja Unida Deus Proverá a guardar meus dados e mensagens para atendimento e a me enviar mensagens pelo WhatsApp, conforme a LGPD. Posso pedir a exclusão a qualquer momento.</span>
      </label>
      <a href="/privacidade" target="_blank" rel="noopener noreferrer" data-testid="register-privacy-link" className="-mt-2 block text-[15px] font-semibold text-zap-deep underline underline-offset-2">Ler a Política de Privacidade</a>
      {!valid && (f.name || f.whatsapp) && (
        <p data-testid="register-hint" className="text-[15px] text-zap-ink/70">Para continuar: nome e sobrenome, WhatsApp com DDD, cidade, estado e a autorização marcada.</p>
      )}
      {error && <p data-testid="register-error" className="text-[15px] font-medium text-red-700">{error}</p>}
      <Button type="submit" data-testid="register-submit" disabled={!valid || busy} className="h-14 w-full text-[17px] font-bold">
        {busy && <Loader2 className="mr-2 h-5 w-5 animate-spin" />} CONFIRMAR E ENVIAR
      </Button>
    </form>
  );
};

const RecoverForm = ({ onDone }) => {
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [withCode, setWithCode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const digits = phone.replace(/\D/g, "");
  const ready = digits.length >= 10 && (withCode ? code.length >= 8 : name.trim().split(/\s+/).length >= 2);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = withCode
        ? await publicApi.post("/public/recover", { whatsapp: digits, code, origin: getOrigin() })
        : await publicApi.post("/public/return", { whatsapp: digits, name, origin: getOrigin() });
      setPToken(data.token);
      sessionStorage.removeItem(FORM_KEY);
      await onDone(withCode ? "recover" : "return");
    } catch (err) {
      setError(errMsg(err, "Sem conexão. Tente novamente; sua mensagem continua guardada."));
    }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="recover-form">
      {!withCode && <Field id="rec-name" label="Nome completo"><Input id="rec-name" data-testid="recover-name" className={inputCls} autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} /></Field>}
      <Field id="rec-wa" label="WhatsApp com DDD"><Input id="rec-wa" data-testid="recover-whatsapp" className={inputCls} inputMode="tel" value={maskPhone(phone)} onChange={(e) => setPhone(e.target.value)} /></Field>
      {withCode ? (
        <Field id="rec-code" label="Código de recuperação"><Input id="rec-code" data-testid="recover-code" className={`${inputCls} font-mono uppercase tracking-widest`} placeholder="XXXX-XXXX" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} /></Field>
      ) : <p className="text-[15px] text-muted-foreground">Use o mesmo nome e o mesmo WhatsApp do primeiro cadastro. Sua conversa volta na hora.</p>}
      {error && <p data-testid="recover-error" className="text-[15px] font-medium text-red-700">{error}</p>}
      <Button type="submit" data-testid="recover-submit" disabled={busy || !ready} className="h-14 w-full text-[17px] font-bold">
        {busy && <Loader2 className="mr-2 h-5 w-5 animate-spin" />} ENTRAR E ENVIAR
      </Button>
      <button type="button" data-testid="recover-toggle-code" onClick={() => { setWithCode(!withCode); setError(""); }}
        className="h-11 w-full text-[15px] font-semibold text-zap-deep underline underline-offset-2">
        {withCode ? "Entrar com nome e WhatsApp" : "Tenho o código de recuperação"}
      </button>
    </form>
  );
};

export const RegisterDialog = ({ open, onClose, onDone }) => {
  const [mode, setMode] = useState("new");
  const tab = (m, label) => (
    <button type="button" data-testid={`register-tab-${m}`} onClick={() => setMode(m)}
      className={`h-11 flex-1 rounded-md text-[15px] font-semibold transition-colors ${mode === m ? "bg-white text-zap-deep shadow-sm" : "text-zap-ink/60"}`}>{label}</button>
  );
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="register-dialog" className="max-h-[92vh] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-zap-deep">Identificação</DialogTitle>
          <DialogDescription className="text-[16px]">{mode === "new" ? "Somente na primeira vez." : "Entre com o mesmo nome e WhatsApp do seu cadastro."} Sua mensagem está guardada e será enviada em seguida.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-1 rounded-lg bg-zap-mist p-1">{tab("new", "Primeiro acesso")}{tab("recover", "Já tenho cadastro")}</div>
        {mode === "new" ? (
          <RegisterForm onDone={onDone} />
        ) : (
          <RecoverForm onDone={onDone} />
        )}
      </DialogContent>
    </Dialog>
  );
};
